// PRD-17 §FR-13..15 — Frontend-only demo data.
//
// Replace with real `GET /api/direction/overview/` per PRD-17 §FR-13 when
// the backend ships. The shape returned here IS the contract — keep it
// stable. To swap, replace the body of `api.ts::directionApi.overview`
// with the axios call and delete this file.
//
// All randomness goes through a seeded LCG (deterministic across reloads
// AND across language switches). The seed is wired off the trimester
// argument so toggling the selector reveals a different — but stable —
// snapshot.

import type {
  AvatarColor,
  DirectionActivityCell,
  DirectionAtRiskRow,
  DirectionDormantProfessor,
  DirectionFacultyRow,
  DirectionInsight,
  DirectionKpis,
  DirectionOverview,
  DirectionSubjectRow,
  DirectionTrendPoint,
  FacultyStatus,
  Trend,
  TrimesterKey,
} from "./types"

// Seeded linear-congruential generator. Same seed → same stream.
function makeRng(seed: number) {
  let state = seed >>> 0
  return () => {
    state = (state * 1664525 + 1013904223) >>> 0
    return state / 0x1_0000_0000
  }
}

const FACULTY_NAMES: ReadonlyArray<string> = [
  "Ana García Vargas",
  "Luis Pérez Hernández",
  "Beatriz Soto Mendoza",
  "Diego Cruz Ramírez",
  "Ema López Castro",
  "Fernando Ruiz Aguilar",
  "Gabriela Méndez Flores",
  "Héctor Domínguez Salinas",
  "Isabel Romero Ortiz",
  "Jorge Castillo Vega",
  "Karla Reyes Navarro",
  "Mauricio Torres Ibáñez",
  "Natalia Vázquez Silva",
  "Óscar Jiménez Acosta",
  "Paola Núñez Cervantes",
  "Quintín Ramos Estrada",
  "Rebeca Sandoval Bravo",
  "Salvador Gómez Lara",
  "Teresa Aguirre Peña",
  "Ulises Bautista Trejo",
  "Valeria Camacho Rangel",
  "Wenceslao Delgado Ovando",
  "Ximena Espinoza Quiroz",
  "Yolanda Figueroa Roldán",
]

const STUDENT_FIRST_NAMES = [
  "Andrés",
  "Brenda",
  "Camila",
  "Daniela",
  "Emilio",
  "Fátima",
  "Gerardo",
  "Hugo",
  "Iván",
  "Julieta",
  "Kevin",
  "Lucía",
  "Mateo",
  "Nadia",
  "Oliver",
  "Paula",
  "Renata",
  "Santiago",
  "Tania",
  "Violeta",
]

const STUDENT_LAST_NAMES = [
  "Mendoza",
  "Ortiz",
  "Salinas",
  "Trejo",
  "Vega",
  "Acosta",
  "Bravo",
  "Cruz",
  "Domínguez",
  "Estrada",
  "Flores",
  "Galván",
  "Herrera",
  "Ibarra",
  "Juárez",
  "Lara",
]

const COLORS: ReadonlyArray<AvatarColor> = ["purple", "blue", "green", "orange"]

// PRD-37 — subjects are now school-defined free text (no fixed enum), so
// the mock data uses plain display names directly, rendered as-is (no i18n).
const SUBJECTS = ["Matemáticas", "Español", "Ciencias Naturales", "Historia", "Inglés"] as const

const TRENDS: ReadonlyArray<Trend> = ["improving", "stable", "declining"]

function initialsOf(fullName: string): string {
  const parts = fullName.split(" ").filter(Boolean)
  const first = parts[0]?.[0] ?? ""
  const second = parts[parts.length - 1]?.[0] ?? ""
  return (first + second).toUpperCase()
}

function pick<T>(arr: ReadonlyArray<T>, rng: () => number): T {
  return arr[Math.floor(rng() * arr.length)]!
}

function clamp(n: number, min: number, max: number) {
  return Math.max(min, Math.min(max, n))
}

function round1(n: number) {
  return Math.round(n * 10) / 10
}

function statusFromMetrics(avg: number, delivery: number, atRisk: number): FacultyStatus {
  if (avg < 7 || delivery < 75 || atRisk >= 5) return "concern"
  if (avg < 8 || delivery < 88 || atRisk >= 2) return "watch"
  return "strong"
}

// Anchor date for deterministic mock content. Decoupled from `new Date()`
// so screenshots & demo videos don't drift over time.
const ANCHOR_ISO = "2026-05-09T08:00:00.000-06:00"
const ANCHOR_MS = new Date(ANCHOR_ISO).getTime()
const DAY_MS = 24 * 60 * 60 * 1000

function isoDateOffset(daysAgo: number): string {
  return new Date(ANCHOR_MS - daysAgo * DAY_MS).toISOString().slice(0, 10)
}

function isoTsOffset(daysAgo: number, hour = 9): string {
  const d = new Date(ANCHOR_MS - daysAgo * DAY_MS)
  d.setHours(hour, 0, 0, 0)
  return d.toISOString()
}

// ── Faculty ─────────────────────────────────────────────────────────────

function buildFaculty(rng: () => number, isCurrent: boolean): DirectionFacultyRow[] {
  return FACULTY_NAMES.map((name, idx) => {
    const baseAvg = 6.4 + rng() * 2.6 // 6.4 – 9.0
    const drift = isCurrent ? rng() * 0.6 - 0.2 : rng() * 0.4 - 0.4
    const avg = round1(clamp(baseAvg + drift, 5.5, 9.6))
    const delivery = Math.round(clamp(70 + rng() * 28 + (isCurrent ? 1 : -2), 60, 99))
    const classrooms = 2 + Math.floor(rng() * 5) // 2-6
    const students = classrooms * (16 + Math.floor(rng() * 12)) // ~16-28 per salon
    const atRisk = Math.floor(students * (avg < 7 ? 0.16 : avg < 8 ? 0.07 : 0.025))
    const trend: Trend = avg >= 8 ? "improving" : avg < 7 ? "declining" : pick(TRENDS, rng)
    const lastSentDays = Math.floor(rng() * 30)
    return {
      professor_id: `fac-${(idx + 1).toString().padStart(2, "0")}`,
      name,
      initials: initialsOf(name),
      color: COLORS[idx % COLORS.length]!,
      classrooms,
      students,
      avg_grade: avg,
      delivery_rate: delivery,
      at_risk_count: atRisk,
      trend,
      last_material_sent_at: lastSentDays > 21 ? null : isoTsOffset(lastSentDays),
      status: statusFromMetrics(avg, delivery, atRisk),
      primary_subject: SUBJECTS[idx % SUBJECTS.length]!,
    }
  })
}

// ── KPIs ────────────────────────────────────────────────────────────────

function buildKpis(faculty: DirectionFacultyRow[], isCurrent: boolean): DirectionKpis {
  const totalProfessors = faculty.length
  const totalClassrooms = faculty.reduce((s, f) => s + f.classrooms, 0)
  const totalStudents = faculty.reduce((s, f) => s + f.students, 0)
  const weighted = faculty.reduce(
    (acc, f) => {
      acc.avg += f.avg_grade * f.students
      acc.del += f.delivery_rate * f.students
      acc.atr += f.at_risk_count
      return acc
    },
    { avg: 0, del: 0, atr: 0 },
  )
  const avgGrade = round1(weighted.avg / totalStudents)
  const deliveryRate = Math.round(weighted.del / totalStudents)
  const atRiskCount = weighted.atr
  const inactiveProfessors = faculty.filter((f) => f.last_material_sent_at === null).length

  // Deltas tell a small narrative for the demo: the institution gained
  // students and improved its average, but at-risk grew slightly because
  // a couple of professors fell behind on deliveries.
  return {
    total_professors: totalProfessors,
    total_classrooms: totalClassrooms,
    total_students: totalStudents,
    avg_grade: avgGrade,
    delivery_rate: deliveryRate,
    at_risk_count: atRiskCount,
    inactive_professors: inactiveProfessors,
    delta_total_professors: isCurrent ? -1 : 0,
    delta_total_students: isCurrent ? 12 : -8,
    delta_avg_grade: isCurrent ? 0.3 : -0.1,
    delta_delivery_rate: isCurrent ? -2 : 1,
    delta_at_risk_count: isCurrent ? 5 : -3,
    delta_inactive_professors: isCurrent ? 1 : -1,
  }
}

// ── Subjects ────────────────────────────────────────────────────────────

function buildSubjects(faculty: DirectionFacultyRow[]): DirectionSubjectRow[] {
  const groups = new Map<string, { sum: number; count: number; classrooms: number }>()
  for (const f of faculty) {
    const cur = groups.get(f.primary_subject) ?? { sum: 0, count: 0, classrooms: 0 }
    cur.sum += f.avg_grade * f.students
    cur.count += f.students
    cur.classrooms += f.classrooms
    groups.set(f.primary_subject, cur)
  }
  return SUBJECTS.map((subject) => {
    const g = groups.get(subject) ?? { sum: 0, count: 1, classrooms: 0 }
    return {
      subject,
      classrooms: g.classrooms,
      avg_grade: round1(g.sum / g.count),
    }
  }).sort((a, b) => b.avg_grade - a.avg_grade)
}

// ── Trend (12 weeks) ────────────────────────────────────────────────────

function buildTrend(rng: () => number): DirectionTrendPoint[] {
  const points: DirectionTrendPoint[] = []
  let cur = 7.4
  let prev = 7.0
  let curDel = 84
  let prevDel = 86
  for (let week = 1; week <= 12; week++) {
    cur = round1(clamp(cur + (rng() * 0.5 - 0.15), 6.5, 9.0))
    prev = round1(clamp(prev + (rng() * 0.4 - 0.2), 6.0, 8.6))
    curDel = Math.round(clamp(curDel + (rng() * 4 - 1.4), 70, 96))
    prevDel = Math.round(clamp(prevDel + (rng() * 3 - 1.6), 65, 94))
    points.push({
      week_index: week,
      current_avg: cur,
      previous_avg: prev,
      current_delivery: curDel,
      previous_delivery: prevDel,
    })
  }
  return points
}

// ── At-risk top 10 ──────────────────────────────────────────────────────

function buildAtRisk(rng: () => number, faculty: DirectionFacultyRow[]): DirectionAtRiskRow[] {
  const concernFaculty = faculty.filter((f) => f.status !== "strong").slice(0, 6)
  const rows: DirectionAtRiskRow[] = []
  for (let i = 0; i < 10; i++) {
    const fac = concernFaculty[i % Math.max(1, concernFaculty.length)] ?? faculty[0]!
    const first = pick(STUDENT_FIRST_NAMES, rng)
    const last = pick(STUDENT_LAST_NAMES, rng)
    const studentName = `${first} ${last}`
    const avg = round1(4.2 + rng() * 1.7) // 4.2 – 5.9
    rows.push({
      student_id: `stu-${(i + 1).toString().padStart(3, "0")}`,
      name: studentName,
      initials: initialsOf(studentName),
      color: COLORS[i % COLORS.length]!,
      classroom_id: `cls-${(i + 1).toString().padStart(3, "0")}`,
      classroom_name: `${fac.primary_subject} ${1 + (i % 3)}°${["A", "B", "C"][i % 3]}`,
      professor_name: fac.name,
      avg_grade: avg,
      trend: rng() < 0.4 ? "stable" : "declining",
      consecutive_missed: 1 + Math.floor(rng() * 4),
    })
  }
  return rows.sort((a, b) => a.avg_grade - b.avg_grade)
}

// ── Insights (curated; payload only — UI maps kind → i18n key) ─────────

function buildInsights(faculty: DirectionFacultyRow[], rng: () => number): DirectionInsight[] {
  const winner = faculty.find((f) => f.avg_grade >= 8.4) ?? faculty[0]!
  const dormant =
    faculty.find((f) => f.last_material_sent_at === null) ?? faculty[faculty.length - 1]!

  const win: DirectionInsight = {
    id: "ins-win",
    kind: "win",
    payload: {
      classroom_name: `${winner.primary_subject} 2°A`,
      subject: winner.primary_subject,
      delta: round1(0.8 + rng() * 0.6),
    },
  }
  const alert: DirectionInsight = {
    id: "ins-alert",
    kind: "alert",
    payload: { count: 3 + Math.floor(rng() * 4) },
  }
  const milestone: DirectionInsight = {
    id: "ins-milestone",
    kind: "milestone",
    payload: { percent: 88 + Math.floor(rng() * 7) },
  }
  const dormantInsight: DirectionInsight = {
    id: "ins-dormant",
    kind: "dormant",
    payload: { professor_name: dormant.name, days: 14 + Math.floor(rng() * 8) },
  }
  return [win, alert, milestone, dormantInsight]
}

// ── Activity heatmap (last 30 days) ─────────────────────────────────────

function buildActivity(rng: () => number): DirectionActivityCell[] {
  const cells: DirectionActivityCell[] = []
  for (let d = 29; d >= 0; d--) {
    const date = isoDateOffset(d)
    const dow = new Date(date).getUTCDay()
    // Fewer materials on weekends; midweek peaks.
    const base = dow === 0 || dow === 6 ? 0 : dow === 2 || dow === 3 ? 6 : 3
    const count = Math.max(0, base + Math.floor(rng() * 6) - 1)
    cells.push({ date, count })
  }
  return cells
}

// ── Dormant professors ──────────────────────────────────────────────────

function buildDormant(faculty: DirectionFacultyRow[]): DirectionDormantProfessor[] {
  return faculty
    .filter((f) => f.last_material_sent_at === null)
    .slice(0, 5)
    .map((f, idx) => ({
      professor_id: f.professor_id,
      name: f.name,
      initials: f.initials,
      color: f.color,
      days_inactive: 14 + idx * 3,
    }))
}

// ── Top-level builder ───────────────────────────────────────────────────

const TRIMESTER_LABELS_ES: Record<TrimesterKey, string> = {
  current: "Trimestre 2 · 2025-2026",
  previous: "Trimestre 1 · 2025-2026",
}

const TRIMESTER_LABELS_EN: Record<TrimesterKey, string> = {
  current: "Term 2 · 2025-2026",
  previous: "Term 1 · 2025-2026",
}

const SEEDS: Record<TrimesterKey, number> = {
  current: 0xc11dc011, // stable
  previous: 0xa11deea,
}

export function buildMockOverview(trimester: TrimesterKey): DirectionOverview {
  const isCurrent = trimester === "current"
  const rng = makeRng(SEEDS[trimester])
  const faculty = buildFaculty(rng, isCurrent)
  const kpis = buildKpis(faculty, isCurrent)
  const subjects = buildSubjects(faculty)
  const trend = buildTrend(rng)
  const atRiskTop = buildAtRisk(rng, faculty)
  const insights = buildInsights(faculty, rng)
  const activity = buildActivity(rng)
  const dormant = buildDormant(faculty)

  return {
    trimester,
    trimester_label_es: TRIMESTER_LABELS_ES[trimester],
    trimester_label_en: TRIMESTER_LABELS_EN[trimester],
    institution_name: "Cecytem 05 — plantel Guacamayas",
    generated_at: isoTsOffset(0, 7),
    kpis,
    faculty,
    subjects,
    trend,
    at_risk_top: atRiskTop,
    insights,
    activity,
    dormant_professors: dormant,
  }
}
